import { useMemo, type FC, useState } from "react"
import { useFormikContext } from "formik"
import {
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Icon,
  Link,
  Spacer,
  Stack,
  StackDivider,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import invariant from "tiny-invariant"
import { isAddress } from "web3-utils"
import { formatUnits } from "ethers/lib/utils.js"
import BigNumber from "bignumber.js"
import { BigNumber as EtherBigNumber } from "@ethersproject/bignumber"
import flatten from "lodash.flatten"

import TallyFeeInformationModal from "createProposal/components/TallyFeeInformationModal"
import type { Action } from "createProposal/action/types"
import type { ExecutableCallInput } from "query/graphql"
import {
  ExecutableCallType,
  useTransferTokensReceiptQuery,
} from "query/graphql"
import TransferNativeAssetAction from "createProposal/action/TransferNativeAssetAction"
import TransferErc20Action from "createProposal/action/TransferErc20Action"
import CustomAction from "createProposal/action/CustomAction"
import { useCreateProposal } from "createProposal/providers/CreateProposalProvider"
import type { CreateProposalFormValues } from "createProposal/types/form"
import SwapAction from "createProposal/action/SwapAction"
import TransferArbitrumErc20Action from "createProposal/action/TransferArbitrumErc20Action"
import ArrowRight from "ui/components/icons/ArrowRight"
import { addressToAccountId } from "web3/helpers/transformers"
import { getMainnetReference } from "web3/helpers/chainReference"
import { isSameAddress, shortenIfAddress } from "web3/helpers/address"
import { EXTERNAL_ROUTES, ROUTES } from "common/constants/routes"
import { getChain } from "web3/helpers/chains"
import { labelNumber } from "common/helpers/number"
import { LayerPlus } from "ui/components/icons/font-awesome/LayerPlus"
import {
  formatFeesForDisplay,
  getTotalFeesFromActions,
  shouldRenderReceiptComponent,
} from "createProposal/helpers/fee"
import ProposalCommand from "proposal/components/ProposalCommand"
import { useDevice } from "common/hooks/useDevice"
import { isValidAccountId } from "web3/helpers/accountId"
import { pluralize, simplifySignature, subString } from "common/helpers/string"
import { ChevronUp } from "ui/components/icons/font-awesome/ChevronUp"
import { ChevronDown } from "ui/components/icons/font-awesome/ChevronDown"
import { getContractNameFromAddressBook } from "createProposal/constants/address-book"
import { ArrowUpRight } from "ui/components/icons/font-awesome/ArrowUpRight"

type CreateProposalReceiptsProps = {
  chainId: string
}

const CreateProposalReceipts: FC<CreateProposalReceiptsProps> = ({
  chainId,
}) => {
  const [showExecutable, setShowExecutable] = useState<boolean>(false)
  const { values } = useFormikContext<CreateProposalFormValues>()

  const { onLargeDevice, onLittleDevice } = useDevice()

  const { actions } = values

  invariant(actions, "'actions' must be defined")

  const shouldShowReceipts = shouldRenderReceiptComponent(actions)

  const { tokenBalances } = useCreateProposal()

  const tallyFeeInformationModal = useDisclosure()

  // Helper function to calculate total fees

  const feeInfo = getTotalFeesFromActions(actions, tokenBalances)

  const feeLabel = formatFeesForDisplay(feeInfo)

  if (actions.every((action) => action.recipe === ExecutableCallType.Empty))
    return null

  if (!shouldShowReceipts) return null

  const executableCalls = flatten(
    actions.map((action) => action.encodeForTallyApi()),
  ).filter((call): call is ExecutableCallInput => call !== undefined)

  return (
    <Stack width={{ base: "100%", sm: "750px" }}>
      <Stack
        bg="white"
        borderRadius="md"
        borderWidth="1px"
        maxW="700px"
        minW="full"
        spacing={0}
      >
        <Stack
          align="center"
          borderBottomWidth="1px"
          direction="row"
          px={{ base: 2, sm: 4 }}
          py={1}
          w="full"
          width="100%"
        >
          <Text fontWeight="md">Actions</Text>
          <Spacer />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowExecutable(!showExecutable)}
          >
            {showExecutable ? "Summary" : "Raw"}
          </Button>
        </Stack>

        {showExecutable &&
        Boolean(executableCalls) &&
        executableCalls.length > 0 ? (
          <Stack align="flex-end" p={5}>
            <Stack className="scrollbar" spacing={4} w="full">
              {executableCalls.map(
                ({ calldata, signature, target, value }, i) => {
                  return (
                    <ProposalCommand
                      key={`target_${target}_executable_call`}
                      isDraftProposal
                      calldata={calldata || ""}
                      chainId={chainId}
                      index={i}
                      signature={signature}
                      target={target}
                      value={value}
                    />
                  )
                },
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack divider={<StackDivider borderColor="gray.200" />} spacing={0}>
            <Flex align="center" display={onLargeDevice} p={5}>
              <Text
                color="#475467"
                fontSize="14px"
                fontWeight={400}
                lineHeight="20px"
                width="25%"
              >
                Type
              </Text>
              <Text
                color="#475467"
                fontSize="14px"
                fontWeight={400}
                lineHeight="20px"
                width="30%"
              >
                Address
              </Text>
              <Text
                color="#475467"
                fontSize="14px"
                fontWeight={400}
                lineHeight="20px"
                width="45%"
              >
                Details
              </Text>
            </Flex>

            {actions.map((action, idx) => (
              <ReceiptRow key={`action=${idx}`} action={action} />
            ))}
            {feeInfo.length > 0 ? (
              <Stack
                align="center"
                direction={{ base: "column", md: "row" }}
                justifyContent="flex-end"
                px={5}
                py={3}
                w="full"
              >
                <Text
                  display={onLargeDevice}
                  fontSize="14px"
                  textStyle="body.regular.md"
                >
                  {feeLabel}
                </Text>
                <Text display={onLittleDevice} textStyle="body.regular.sm">
                  {feeLabel}
                </Text>
                <Text
                  cursor="pointer"
                  textDecoration="underline"
                  textStyle="body.regular.md"
                  onClick={tallyFeeInformationModal.onOpen}
                >
                  What is the fee for?
                </Text>
              </Stack>
            ) : null}
          </Stack>
        )}
      </Stack>
      <TallyFeeInformationModal
        isOpen={tallyFeeInformationModal.isOpen}
        onClose={tallyFeeInformationModal.onClose}
      />
    </Stack>
  )
}

type ReceiptRowProps = {
  action: Action
}

const ReceiptRow: FC<ReceiptRowProps> = ({ action }) => {
  const isTransferRecipe =
    action instanceof TransferNativeAssetAction ||
    action instanceof TransferErc20Action ||
    action instanceof TransferArbitrumErc20Action

  const isCustomRecipe = action instanceof CustomAction

  const isSwapRecipe = action instanceof SwapAction

  if (isTransferRecipe) return <TransferReceipt action={action} />

  if (isCustomRecipe) return <CustomReceipt action={action} />

  if (isSwapRecipe) return <SwapReceipt action={action} />

  return null
}

type TransferReceiptProps = {
  action:
    | TransferNativeAssetAction
    | TransferErc20Action
    | TransferArbitrumErc20Action
}

const TransferReceipt: FC<TransferReceiptProps> = ({ action }) => {
  const { value } = action

  const { governanceChainId, tokenBalances } = useCreateProposal()
  const { onLargeDevice, onLittleDevice } = useDevice()

  const recipientAddress = useMemo(() => {
    if (
      action instanceof TransferErc20Action ||
      action instanceof TransferArbitrumErc20Action
    ) {
      return Boolean(action.recipient?.trim())
        ? (action.recipient?.trim() as string)
        : "0x"
    }

    const target = action.getTarget()

    if (target.trim() === "") return "0x"

    return target
  }, [action])

  const recipientAccountId = addressToAccountId(
    recipientAddress,
    getMainnetReference(),
  )

  const { data } = useTransferTokensReceiptQuery(
    { accountIds: recipientAccountId },
    {
      enabled:
        Boolean(recipientAccountId) && isValidAccountId(recipientAccountId),
    },
  )

  const targetAccount = data?.accounts.find((account) =>
    isSameAddress(account.address, recipientAddress),
  )

  const tokenValue = Number(value)

  const chain = getChain(governanceChainId)

  const token = useMemo(() => {
    const { recipe, targetAddress } = action

    if (recipe === ExecutableCallType.Nativetransfer) return

    return tokenBalances?.tokens?.find((token) =>
      isSameAddress(token.tokenInfo.address, targetAddress as string),
    )
  }, [action, tokenBalances])

  const tokenLabel = useMemo(() => {
    if (!token) return chain?.nativeCurrency?.symbol ?? ""

    return token.tokenInfo.symbol
  }, [token, chain])

  const tokenPrice = useMemo(() => {
    if (!token) return tokenBalances?.nativeAsset.tokenInfo.price.rate

    return token.tokenInfo.price.rate
  }, [token, tokenBalances])

  const recipientLabel = useMemo(() => {
    if (targetAccount?.name && !isAddress(targetAccount?.name))
      return targetAccount?.name

    return undefined
  }, [targetAccount?.name])

  if (!isAddress(recipientAddress)) return null

  return (
    <Stack>
      <Flex align="center" display={onLargeDevice} p={5}>
        <HStack align="center" spacing={3} w="25%">
          <Icon as={ArrowRight} fill="#344054" h={4} w={4} />
          <Text
            color="#344054"
            fontSize="14px"
            fontWeight={500}
            lineHeight="20px"
          >
            Transfer
          </Text>
        </HStack>
        <Stack
          direction="row"
          overflow="hidden"
          spacing={2}
          textOverflow="ellipsis"
          w="30%"
          whiteSpace="nowrap"
        >
          <Stack justify="center" spacing={0}>
            {recipientLabel ? (
              <Text
                color="#000000"
                fontSize="14px"
                fontWeight="medium"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {subString(recipientLabel, 28)}
              </Text>
            ) : null}
            <HStack p={0}>
              {!isAddress(targetAccount?.name ? targetAccount.name : "") && (
                <Text color="gray.500" fontSize="14px" fontWeight="medium">
                  {shortenIfAddress(targetAccount?.address)}
                </Text>
              )}
              <Link isExternal href={ROUTES.profile(targetAccount?.address)}>
                <Icon
                  as={ArrowUpRight}
                  color="#98A2B3"
                  h={2.5}
                  mb={0.5}
                  w={2.5}
                />
              </Link>
            </HStack>
          </Stack>
        </Stack>
        <Box maxW="30%" px={2} py={1}>
          <Text
            color="gray.700"
            fontSize="14px"
            fontWeight="bold"
            overflow="hidden"
            textAlign="center"
            textOverflow="ellipsis"
            textStyle="body.bold.lg"
            whiteSpace="nowrap"
          >
            {tokenPrice ? (
              <>
                {`${labelNumber(tokenValue)} ${tokenLabel}
          ($${labelNumber(tokenValue * tokenPrice)} USD)`}
              </>
            ) : (
              `${labelNumber(tokenValue)} ${tokenLabel}`
            )}
          </Text>
        </Box>
      </Flex>
      <Stack display={onLittleDevice} px={5} py={2} spacing={0}>
        <HStack align="center" py={2}>
          <Icon as={ArrowRight} fill="#344054" h={3} w={3} />
          <Text
            color="#344054"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
          >
            Transfer
          </Text>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Address
          </Text>
          <HStack spacing={2}>
            <Stack p={0}>
              {recipientLabel ? (
                <Text textStyle="body.bold.md">
                  {subString(recipientLabel, 16)}
                </Text>
              ) : null}
              <HStack p={0} spacing={0}>
                {!isAddress(targetAccount?.name ? targetAccount.name : "") && (
                  <Text color="#667085" textStyle="body.regular.md">
                    {shortenIfAddress(targetAccount?.address)}
                  </Text>
                )}
                <Link isExternal href={ROUTES.profile(targetAccount?.address)}>
                  <Icon
                    as={ArrowUpRight}
                    color="#98A2B3"
                    h={2.5}
                    mb={0.5}
                    w={2.5}
                  />
                </Link>
              </HStack>
            </Stack>
          </HStack>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Details
          </Text>
          <Box borderRadius="md" px={2} py={1}>
            <Text
              fontWeight="bold"
              overflow="hidden"
              textAlign="center"
              textOverflow="ellipsis"
              textStyle="sm"
              whiteSpace="nowrap"
            >
              {tokenPrice ? (
                <>
                  {`${labelNumber(tokenValue)} ${tokenLabel}
              ($${labelNumber(tokenValue * tokenPrice)} USD)`}
                </>
              ) : (
                `${labelNumber(tokenValue)} ${tokenLabel}`
              )}
            </Text>
          </Box>
        </HStack>
      </Stack>
    </Stack>
  )
}

type CustomReceiptProps = {
  action: CustomAction
}

const CustomReceipt: FC<CustomReceiptProps> = ({ action }) => {
  const { governanceId } = useCreateProposal()
  const { onLargeDevice, onLittleDevice } = useDevice()
  const [displayParams, setDisplayParams] = useState(false)
  const { targetAddress, selectedAbiMethod } = action

  if (!targetAddress) return null

  const method =
    selectedAbiMethod?.name ??
    simplifySignature(action?.originalExecutableAction?.signature ?? "") ??
    undefined

  if (!method) return null

  const blockExplorerLink = EXTERNAL_ROUTES.etherscan.address(
    getMainnetReference(),
    targetAddress as string,
  )

  const parameters = selectedAbiMethod?.inputs?.map((input) => {
    try {
      if (EtherBigNumber.isBigNumber(input.value)) {
        return {
          name: input.name,
          value: EtherBigNumber.from(input.value).toString(),
        }
      }

      return {
        name: input.name,
        value: input.value,
      }
    } catch {
      return {
        name: input.name,
        value: undefined,
      }
    }
  })

  const contractName = governanceId
    ? getContractNameFromAddressBook(governanceId, targetAddress as string)
    : undefined

  return (
    <>
      <Flex align="center" display={onLargeDevice} p={5} w="full">
        <HStack align="center" spacing={3} w="25%">
          <Icon as={LayerPlus} fill="gray.600" h={4} w={4} />
          <Text
            color="gray.600"
            fontSize="14px"
            fontWeight={500}
            lineHeight="20px"
          >
            Custom
          </Text>
        </HStack>
        <HStack w="30%">
          <Stack direction="column" spacing={0}>
            {contractName ? (
              <Text
                color="#000000"
                fontSize="14px"
                fontWeight="medium"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {contractName}
              </Text>
            ) : null}
            <HStack>
              <Text color="gray.500" fontSize="14px" fontWeight="medium">
                {shortenIfAddress(targetAddress as string)}
              </Text>
              <Link isExternal href={blockExplorerLink}>
                <Icon
                  as={ArrowUpRight}
                  color="#98A2B3"
                  h={2.5}
                  mb={0.5}
                  w={2.5}
                />
              </Link>
            </HStack>
          </Stack>
        </HStack>

        <Box borderRadius="md" px={2} py={1} width="45%">
          <Stack align="center" direction="row" width="100%">
            {method ? (
              <Text
                color="gray.700"
                fontSize="14px"
                fontWeight="bold"
                overflow="hidden"
                textAlign="center"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {method}
              </Text>
            ) : null}

            <Spacer />

            {parameters && parameters?.length > 0 ? (
              <Stack align="center" direction="row" float="right" pr={4}>
                <Button
                  fontWeight="regular"
                  h={2}
                  rightIcon={
                    <Icon
                      as={displayParams ? ChevronUp : ChevronDown}
                      color="#667085"
                      h={3}
                      w={3}
                    />
                  }
                  variant="transparent"
                  w={2}
                  onClick={() => setDisplayParams(!displayParams)}
                >
                  <Text
                    color="#667085"
                    fontSize="12px"
                    fontWeight="regular"
                    textAlign="right"
                  >
                    {parameters.length}{" "}
                    {pluralize(parameters.length, "param", "params")}
                  </Text>
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Box>
      </Flex>
      <Stack display={onLittleDevice} px={5} py={2} spacing={0}>
        <HStack align="center" py={2}>
          <Icon as={LayerPlus} fill="gray.600" h={3} w={3} />
          <Text
            color="gray.600"
            fontSize="14px"
            fontWeight="medium"
            lineHeight="20px"
          >
            Custom
          </Text>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Account
          </Text>
          <HStack>
            <Stack p={0} spacing={0}>
              {contractName ? (
                <Text textStyle="body.bold.md">{contractName}</Text>
              ) : null}

              <HStack p={0}>
                <Text
                  color={contractName ? "#667085" : "default"}
                  fontWeight={contractName ? "regular" : "bold"}
                  textStyle="body.bold.md"
                >
                  {shortenIfAddress(targetAddress as string)}
                </Text>
                <Link isExternal href={blockExplorerLink}>
                  <Icon
                    as={ArrowUpRight}
                    color="#98A2B3"
                    h={2.5}
                    mb={0.5}
                    w={2.5}
                  />
                </Link>
              </HStack>
            </Stack>
          </HStack>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Method
          </Text>
          <Box borderRadius="md" px={2} py={1}>
            {method ? (
              <Text
                fontWeight="bold"
                overflow="hidden"
                textAlign="center"
                textOverflow="ellipsis"
                textStyle="body.bold.md"
                whiteSpace="nowrap"
              >
                {method}
              </Text>
            ) : null}
          </Box>
        </HStack>

        {parameters && parameters?.length > 0 ? (
          <Button
            fontWeight="regular"
            variant="secondary"
            onClick={() => setDisplayParams(!displayParams)}
          >
            {displayParams ? "Hide" : "See"} params ({parameters.length})
          </Button>
        ) : null}
      </Stack>

      {displayParams && parameters && parameters?.length > 0 ? (
        <Stack mb={4} mt={1} mx={4} spacing={0.5}>
          {parameters.map((parameter, idx) => (
            <Text
              key={`param-${idx}`}
              className="clamp-text-3"
              textStyle="code"
              whiteSpace="break-spaces"
            >
              <Code wordBreak="break-word">
                {parameter.name}:{" "}
                <Text as="span" color="gray.800">
                  {parameter.value}
                </Text>
                {idx < parameters.length - 1 ? "," : ""}
              </Code>
            </Text>
          ))}
        </Stack>
      ) : null}
    </>
  )
}

type SwapReceiptProps = {
  action: SwapAction
}

const SwapReceipt: FC<SwapReceiptProps> = ({ action }) => {
  const { onLargeDevice, onLittleDevice } = useDevice()

  const { amountIn, fee, buyToken, quote, sellToken, milkmanContractAddress } =
    action

  if (!buyToken || !quote || !sellToken) return null

  const { decimals: sellDecimals, symbol: sellSymbol } = sellToken
  const { decimals: buyDecimals, symbol: buySymbol } = buyToken
  const { buyAmount } = quote
  const formattedAmountIn = formatUnits(
    new BigNumber(amountIn).plus(fee).toFixed(),
    sellDecimals,
  )
  const out = new BigNumber(buyAmount).toPrecision(4)
  const formattedOut = formatUnits(new BigNumber(out).toString(), buyDecimals)

  const blockExplorerLink = EXTERNAL_ROUTES.etherscan.address(
    getMainnetReference(),
    milkmanContractAddress as string,
  )

  return (
    <>
      <Flex align="center" display={onLargeDevice} p={5} w="full">
        <HStack align="center" spacing={3} w="25%">
          <Icon as={ArrowRight} fill="#344054" h={4} w={4} />
          <Text
            color="#344054"
            fontSize="16px"
            fontWeight={500}
            lineHeight="20px"
          >
            Swap
          </Text>
        </HStack>
        <HStack w="45%">
          <Text textStyle="body.bold.lg">Milkman</Text>
          <Text color="#667085" textStyle="body.regular.lg">
            {`(${shortenIfAddress(milkmanContractAddress)})`}
          </Text>
          <Link isExternal href={blockExplorerLink}>
            <Icon as={ArrowUpRight} color="#98A2B3" h={2.5} mb={0.5} w={2.5} />
          </Link>
        </HStack>
        <Box borderRadius="md" maxW="30%" px={2} py={1}>
          <Text
            fontWeight="bold"
            overflow="hidden"
            textAlign="center"
            textOverflow="ellipsis"
            textStyle="body.bold.lg"
            whiteSpace="nowrap"
          >
            {`${formattedAmountIn} ${sellSymbol} for ${formattedOut} ${buySymbol}`}
          </Text>
        </Box>
      </Flex>
      <Stack display={onLittleDevice} px={5} py={2} spacing={0}>
        <HStack align="center" py={2}>
          <Icon as={ArrowRight} fill="gray.600" h={3} w={3} />
          <Text
            color="gray.600"
            fontSize="14px"
            fontWeight="medium"
            lineHeight="20px"
          >
            Swap
          </Text>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Address
          </Text>
          <HStack>
            <Stack spacing={0}>
              <Text textStyle="body.bold.md">Milkman</Text>
              <Text color="#667085" textStyle="body.regular.md">
                {`(${shortenIfAddress(milkmanContractAddress)})`}
              </Text>
            </Stack>
            <Link isExternal href={blockExplorerLink}>
              <Icon
                as={ArrowUpRight}
                color="#98A2B3"
                h={2.5}
                mb={0.5}
                w={2.5}
              />
            </Link>
          </HStack>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Details
          </Text>
          <Box borderRadius="md" px={2} py={1}>
            <Text
              fontWeight="bold"
              overflow="hidden"
              textAlign="center"
              textOverflow="ellipsis"
              textStyle="body.bold.md"
              whiteSpace="nowrap"
            >
              {`${formattedAmountIn} ${sellSymbol} for ${formattedOut} ${buySymbol}`}
            </Text>
          </Box>
        </HStack>
      </Stack>
    </>
  )
}

export default CreateProposalReceipts
